import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Grid,
    InputAdornment,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import axios from "axios";
import { API_URL } from "./config";
import {
    Email,
    Lock,
    Person,
    PersonAdd,
    VerifiedUser,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { AdminPageShell, AdminStat, AdminSurface } from "./admin/AdminPageShell";

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '18px',
        bgcolor: '#fff',
    },
};

const CreateUser = () => {
    const [roles, setRoles] = useState([]);
    const [formData, setFormData] = useState({
        email: "",
        role: "",
        password: "",
        confirmPassword: "",
    });
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await axios.get(API_URL + "create-user.php", {
                    withCredentials: true,
                });
                if (response.data.success) {
                    setRoles(response.data.roles);
                } else {
                    setErrorMessage(response.data.message || "Failed to fetch roles.");
                }
            } catch (error) {
                setErrorMessage("Error fetching roles. Please try again.");
            }
        };

        fetchRoles();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSuccessMessage("");
        setErrorMessage("");

        const formDataObj = new FormData();
        formDataObj.append("email", formData.email);
        formDataObj.append("role", formData.role);
        formDataObj.append("password", formData.password);
        formDataObj.append("confirmPassword", formData.confirmPassword);

        try {
            const response = await axios.post(API_URL + "create-user.php", formDataObj, {
                withCredentials: true,
            });

            if (response.data.success) {
                setSuccessMessage(response.data.message);
                setFormData({
                    email: "",
                    role: "",
                    password: "",
                    confirmPassword: "",
                });
            } else {
                setErrorMessage(
                    response.data.message === "Email already exists."
                        ? "This email is already registered. Please use another email."
                        : response.data.message,
                );
            }
        } catch (error) {
            setErrorMessage("Error creating user. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <AdminPageShell
            icon={<PersonAdd />}
            eyebrow="User management"
            title="Create User"
            description="Set up a new teammate account with the correct role and secure credentials in a clean, consistent admin flow."
            maxWidth="lg"
        >
            <Box
                sx={{
                    display: "grid",
                    gap: 3,
                    gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(240px, 320px) minmax(0, 1fr)" },
                    alignItems: "start",
                    width: "100%",
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Stack spacing={2}>
                        <AdminStat
                            label="Available roles"
                            value={roles.length}
                            helper="Choose the right access level before creating the account."
                        />
                        <AdminSurface>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2d2a5a', mb: 1.5 }}>
                                Before you create
                            </Typography>
                            <Stack spacing={1.25}>
                                <Typography variant="body2" color="text.secondary">
                                    • Use a unique email address for every teammate.
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    • Match the user role to their day-to-day responsibilities.
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    • Ask the user to update their password after first sign-in.
                                </Typography>
                            </Stack>
                        </AdminSurface>
                    </Stack>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <AdminSurface>
                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#2d2a5a' }}>
                                    New user details
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                    Complete the fields below to create the new account.
                                </Typography>
                            </Box>

                            <Box component="form" onSubmit={handleSubmit}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Email address"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            type="email"
                                            fullWidth
                                            required
                                            sx={fieldStyles}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Email color="primary" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="User role"
                                            name="role"
                                            value={formData.role}
                                            onChange={handleInputChange}
                                            select
                                            fullWidth
                                            required
                                            sx={fieldStyles}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <VerifiedUser color="primary" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        >
                                            {roles.map((role) => (
                                                <MenuItem key={role.id} value={role.id} sx={{ py: 1.5 }}>
                                                    <Person sx={{ mr: 1.25, color: 'primary.main' }} />
                                                    {role.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            type="password"
                                            fullWidth
                                            required
                                            sx={fieldStyles}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Lock color="primary" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Confirm password"
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            type="password"
                                            fullWidth
                                            required
                                            sx={fieldStyles}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Lock color="primary" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        fullWidth
                                        size="large"
                                        disabled={loading}
                                        sx={{
                                            mt: 3,
                                            py: 1.6,
                                            borderRadius: '18px',
                                            fontWeight: 700,
                                            fontSize: '1rem',
                                            textTransform: 'none',
                                            background:
                                                'linear-gradient(135deg, #4a90e2 0%, #6a5af9 55%, #9013fe 100%)',
                                            boxShadow: '0 18px 30px rgba(106, 90, 249, 0.24)',
                                        }}
                                    >
                                        {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Create User'}
                                    </Button>
                                </motion.div>
                            </Box>

                            {(successMessage || errorMessage) && (
                                <Stack spacing={1.5}>
                                    {successMessage && (
                                        <Alert severity="success" onClose={() => setSuccessMessage("")}>
                                            {successMessage}
                                        </Alert>
                                    )}
                                    {errorMessage && (
                                        <Alert severity="error" onClose={() => setErrorMessage("")}>
                                            {errorMessage}
                                        </Alert>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    </AdminSurface>
                </Box>
            </Box>
        </AdminPageShell>
    );
};

export default CreateUser;
